import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { OwnerUser, Reservation } from '@/reservation/reservation.model';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { ReservationService } from '@/reservation/reservation.service';
import { finalize, map, Observable, of, take } from 'rxjs';
import { ReservationSummaryApiService } from '@/reservation/reservation-summary/services/reservation-summary-api.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';

@Component({
	selector: 'app-duplicate-user-data-dialog',
	standalone: true,
	imports: [MatTableModule, DatePipe, MatDialogActions, MatButton],
	templateUrl: './duplicate-user-data-dialog.component.html',
	styleUrl: './duplicate-user-data-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DuplicateUserDataDialogComponent {
	readonly displayedColumns: string[] = [
		'id',
		'firstName',
		'lastName',
		'birth',
		'userContacts',
		'userAddresses',
		'isSelected',
	];
	readonly dataSource: { ownerUsers: OwnerUser[]; slotId: number } = inject(MAT_DIALOG_DATA);

	readonly selectedUserId = signal<number>(null);

	private readonly reservationService = inject(ReservationService);
	private readonly reservationId$ = this.reservationService.reservation$.pipe(map((res) => res?.id));
	private readonly apiService = inject(ReservationSummaryApiService);
	private readonly infoboxService = inject(InfoboxService);
	private readonly dialogRef = inject(MatDialogRef<DuplicateUserDataDialogComponent>);
	private readonly dialog = inject(MatDialog);
	private readonly loaderService = inject(LoaderService);

	selectRow(row: OwnerUser): void {
		if (!row) {
			return;
		}

		this.selectedUserId.set(row.id);
	}

	closeDialog(): void {
		this.dialogRef.close();
	}

	submit(): void {
		if (this.dataSource.slotId === null) {
			this.updateUser('owner');
		} else {
			this.updateUser('slot');
		}
	}

	private updateUser(action: 'owner' | 'slot'): void {
		const userId = this.selectedUserId();
		if (!userId) {
			return;
		}

		this.loaderService.show('change-user');

		this.reservationId$.pipe(take(1)).subscribe((reservationId) => {
			const request$: Observable<Reservation | string> =
				action === 'owner'
					? this.apiService.patchReservation(reservationId, {
							ownerUser: userId,
							ownerOrganization: null,
						})
					: this.apiService.patchOfferSlot(this.dataSource.slotId, {
							userData: userId,
						});

			request$.pipe(finalize(() => this.loaderService.hide('change-user'))).subscribe((result) => {
				if (result) {
					this.infoboxService.success(
						action === 'owner' ? 'Pomyślnie zmieniono zgłaszającego' : 'Pomyślnie zmieniono uczestnika',
					);
					this.dialog.closeAll();

					this.apiService.actions().getReservation(reservationId);
				}
			});
		});
	}
}
